import classNames from "classnames";
import React, { useState, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Icon from "../icon/Icon";
import FilterTabs from "./FilterTabs";
import "./styles.styl";
import { useDeliveries } from "./useDeliveries";

const SKELETON_FILTER_COUNT = 3;
const SKELETON_WEEK_COUNT = 2;
const SKELETON_DELIVERY_COUNT = 3;

export default function UpcomingDeliveries({
  subscription,
  displayed,
  delivery: currentDelivery,
  onCloseClick,
  isAuthenticated,
    isStaff,
  onLoginClick,
}) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [collapsedWeeks, setCollapsedWeeks] = useState(new Set());

  const { isLoading, groupedDeliveries: allDeliveries, formatters } = useDeliveries({
    subscription,
    displayed,
    endpoint: "upcoming-deliveries",
    groupBy: "byWeek",
    currentDeliveryId: currentDelivery?.id,
  });

  const intl = useIntl();

  const handleDeliveryClick = useCallback((delivery) => {
    if (delivery.is_current === true) {
      onCloseClick();
    } else if (delivery.id) {
      window.open(`/view/delivery/${delivery.id}`, "_blank");
    } else {
      onLoginClick();
    }
  }, [onCloseClick]);

  const toggleWeek = useCallback((weekKey) => {
    const newCollapsed = new Set(collapsedWeeks);
    if (newCollapsed.has(weekKey)) {
      newCollapsed.delete(weekKey);
    } else {
      newCollapsed.add(weekKey);
    }
    setCollapsedWeeks(newCollapsed);
  }, [collapsedWeeks]);

  const getDeliveryStatus = (delivery) => {
    const statuses = [];

    if (delivery.status === "paused" || delivery.status === "cancelled") {
      statuses.push("paused");
    }
    if (delivery.status === "sold") {
      statuses.push("ordered");
    }
    if (delivery.remaining_days > 0 || delivery.remaining_hours > 0) {
      statuses.push("open");
    }

    return statuses;
  };

  const getVisualStatus = (delivery) => {
    if (delivery.status === "paused") {
      return "paused";
    } else if (delivery.status === "cancelled") {
      return "cancelled";
    } else if (delivery.status === "sold") {
      return "ordered";
    } else if (delivery.remaining_days > 0 || delivery.remaining_hours > 0) {
      return "open";
    }
    return "closed";
  };

  const filterDeliveries = (deliveries) => {
    if (!activeFilter) return deliveries;

    const filtered = {};
    Object.entries(deliveries).forEach(([dateRange, weekDeliveries]) => {
      const filteredWeek = weekDeliveries.filter(delivery => {
        const statuses = getDeliveryStatus(delivery);
        return statuses.includes(activeFilter);
      });

      if (filteredWeek.length > 0) {
        filtered[dateRange] = filteredWeek;
      }
    });

    return filtered;
  };

  const groupedDeliveries = useMemo(() =>
    filterDeliveries(allDeliveries),
    [allDeliveries, activeFilter]
  );

  if (!displayed) return null;

  if (isLoading) {
    return (
      <div className="upcoming-deliveries">
        <div className="filter-container skeleton-filter-container">
          {Array.from({ length: SKELETON_FILTER_COUNT }, (_, i) => (
            <div key={i} className="skeleton-line skeleton-filter-tab"></div>
          ))}
        </div>

        {Array.from({ length: SKELETON_WEEK_COUNT }, (_, i) => (
          <div key={i} className="week-container skeleton-week-container">
            <div className="week-header skeleton-week-header">
              <div className="skeleton-line skeleton-week-number"></div>
              <div className="skeleton-line skeleton-date-range"></div>
              <div className="skeleton-line skeleton-arrow"></div>
            </div>

            <div className="week-deliveries">
              {Array.from({ length: SKELETON_DELIVERY_COUNT }, (_, j) => (
                <div key={j} className="skeleton-delivery-card">
                  <div className="skeleton-weekday-info">
                    <div className="skeleton-line skeleton-weekday"></div>
                    <div className="skeleton-line skeleton-address"></div>
                  </div>
                  <div className="skeleton-date-col">
                    <div className="skeleton-line skeleton-date"></div>
                    <div className="skeleton-line skeleton-deadline"></div>
                  </div>
                  <div className="skeleton-delivery-actions">
                    <div className="skeleton-line skeleton-status-pill"></div>
                    <div className="skeleton-line skeleton-cart-icon"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const hasNoDeliveries = Object.keys(groupedDeliveries).length === 0;

  return (
    <div className="upcoming-deliveries">
      <FilterTabs
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      {hasNoDeliveries ? (
        <div className="no-deliveries">
          <FormattedMessage
            id="noDeliveriesInCategory"
            defaultMessage="No deliveries in this category"
          />
        </div>
      ) : (
        Object.entries(groupedDeliveries).map(([dateRange, weekDeliveries]) => {
        const [startDate, endDate] = dateRange
          .split(" - ")
          .map((date) => new Date(date));
        const weekKey = `week-${weekDeliveries[0].week_number}`;
        const isCollapsed = collapsedWeeks.has(weekKey);

        return (
          <div key={dateRange} className="week-container">
            <div
              className={classNames("week-header", { collapsed: isCollapsed })}
              onClick={() => toggleWeek(weekKey)}
            >
              <span className="week-number">
                {intl.formatMessage(
                  { id: "weekNumber" },
                  { number: weekDeliveries[0].week_number.toString().padStart(2, "0") }
                )}
              </span>
              <span className="date-range">
                {formatters.formatDate(startDate)} - {formatters.formatDate(endDate)}
              </span>
              <div className={classNames("collapse-arrow", { collapsed: isCollapsed })} />
            </div>

            {!isCollapsed && (
              <div className="week-deliveries">
                {weekDeliveries.map((delivery, index) => {
                  const status = getVisualStatus(delivery);

                  return (
                    <div
                      key={delivery.id || `delivery-${index}`}
                      className={classNames("delivery-card", {
                        "is-current": delivery.is_current === true,
                        "readonly": !isAuthenticated,
                      })}
                      onClick={() => handleDeliveryClick(delivery)}
                    >
                      <div className="weekday-info">
                        <div className="weekday">
                          {
                            formatters.formatWeekday(
                              delivery.specific_timeslot_weekday,
                              delivery.generic_timeslot_weekday,
                            )}
                        </div>
                        <div className="delivery-address">
                          {delivery.address_display ||
                           intl.formatMessage({ id: "checkDeliveryArea" })}
                        </div>
                      </div>

                      <div className="date-col">
                        <div className="date">
                          {formatters.formatDate(delivery.timeslot__date)}
                        </div>
                        <div className="deadline-info">
                          {/*{intl.formatMessage({ id: "orderDeadlinePassed" })}*/}
                          {delivery.remaining_days > 0
                            ? intl.formatMessage(
                                { id: "daysUntilDeadline" },
                                { days: delivery.remaining_days }
                              )
                            : delivery.remaining_hours > 0
                            ? intl.formatMessage(
                                { id: "hoursUntilDeadline" },
                                { hours: delivery.remaining_hours }
                              )
                            : intl.formatMessage({ id: "orderDeadlinePassed" })}
                        </div>
                      </div>

                      <div className="delivery-actions">
                        <div className={classNames("status-pill", status)}>
                          <FormattedMessage id={`deliveryStatus.${status}`} />
                        </div>
                        <Icon name="shopping_cart" className="cart-icon" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }))}
    </div>
  );
}
